import React, { useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from "tss-react/mui";

import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ComponentSuspense from "../../../Common/ComponentSuspense";
import WorkspaceService from "../../../../service/workspace";

import { checkIsClusterExist, checkIsSchemaExist, setPrevState } from "../../../../util/common";
import RunJobAsComponent from "../utils/RunJobAsComponent";
import KMKGroupSelect from "../../../Common/KMKGroupSelect";

import { regexFileName } from "../../../../util/regularExpress";

const MyAceEditor = React.lazy(() => import("../../../Common/AceEditor"));

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const Types = [
  { value: 1, text: "CSV" },
  { value: 2, text: "Table" },
];

const useStyles = makeStyles()((theme) => ({
  queryArea: {
    width: "100% !important",
    height: "173px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
  AutoComplete: {
    "& .MuiAutocomplete-listbox": {
      maxWidth: "100%",
      wordBreak: "break-all !important",
    },
  },
  PanelTitle: {
    color: "#ed6c02",
    padding: "24px 24px 12px 0",
    minHeight: 40,
  },
}));

const AdditionalData = (props) => {
  const {
    additionalDataSections,
    additionalState,
    getSchemaList,
    locationClusters,
    locationSchemas,
    locations,
    participantsExcludeVisitor,
    selectedKey,
    setAdditionalState,
    setJobState,
    updateClusterList,
    setSelectedKey,
    setVariableError,
    variableError,
    section,
  } = props;

  const { Workspace_id, Job_id } = useParams();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const Edit_Job_id = useMemo(() => dataPipeline?.editJob?.id ?? null, [dataPipeline]);

  const { classes } = useStyles();
  const queryRef = useRef(null);

  const user = useSelector((state) => state.user.currentUser);
  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const workspace = useSelector((state) => state.workspace);
  const { accessRole, DBName } = workspace.workspace;

  const config = useSelector((state) => state.config);
  const { defaultSchema, defaultCluster } = config;

  const locationType = useMemo(
    () => locations.find(({ value }) => value === additionalState?.location)?.type || "",
    [additionalState.location, locations]
  );

  const connectionClusters = useMemo(() => {
    return locationClusters[additionalState?.location] || [];
  }, [locationClusters, additionalState.location]);

  const fileNameError = useMemo(
    () =>
      additionalDataSections.find(
        (section) =>
          section.key !== selectedKey && section.queryResultAs === 1 && section.fileName === additionalState.fileName
      ),
    [additionalDataSections, additionalState.fileName, selectedKey]
  );

  const { schemaError, clusterError, userNotExist } = useMemo(
    () => ({
      isMissingVariable: variableError[selectedKey]?.isMissingVariable,
      isDuplicate: variableError[selectedKey]?.isDuplicate,
      schemaError: variableError[selectedKey]?.schemaError,
      clusterError: variableError[selectedKey]?.clusterError,
      userNotExist: variableError[selectedKey]?.userNotExist,
    }),
    [selectedKey, variableError]
  );

  const handleUserChange = useCallback(
    (e, { runJobAsUserId, runJobAsUserFullName }) => {
      setAdditionalState((prev) =>
        prev.map((item) =>
          item.key === selectedKey ? { ...item, runJobAsUserId, runJobAsUserFullName, userNotExist: false } : item
        )
      );
      setPrevState(setVariableError, {
        [selectedKey]: { ...variableError[selectedKey], userNotExist: false },
      });
    },
    [selectedKey, setAdditionalState, setVariableError, variableError]
  );

  const handleRunUnderMyName = useCallback(
    (e) => {
      handleUserChange(e, { runJobAsUserId: user.User_id, runJobAsUserFullName: currentUserFullName });
      setPrevState(setVariableError, {
        [selectedKey]: { ...variableError[selectedKey], userNotExist: false },
      });
    },
    [selectedKey, currentUserFullName, handleUserChange, setVariableError, user.User_id, variableError]
  );

  const handleSchemaChange = (e, value) => {
    setAdditionalState((prev) =>
      prev.map((item) =>
        item.key === selectedKey ? { ...item, schema: value?.title || "", database: value?.groupBy || "" } : item
      )
    );

    setPrevState(setVariableError, {
      [selectedKey]: { ...variableError[selectedKey], schemaError: false },
    });
  };

  const handleClusterChange = (e) => {
    setAdditionalState((prev) =>
      prev.map((item) => (item.key === selectedKey ? { ...item, Cluster: e.target.value } : item))
    );

    setPrevState(setVariableError, {
      [selectedKey]: { ...variableError[selectedKey], clusterError: false },
    });
  };

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      if (!location || location === "SNOWFLAKE") return [];
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusters = data?.map((Cluster) => ({ value: Cluster?.name || "" }));
        updateClusterList(location, clusters);
        return clusters;
      }
      updateClusterList(location, []);
      return [];
    },
    [Workspace_id, updateClusterList]
  );

  const handleConnectionChange = useCallback(
    async (location) => {
      setPrevState(setJobState, { loading: true });

      const locationType = locations?.find(({ value }) => value === location)?.type || "";

      // Schemalist already present
      let schemaArray = false;
      let clusterArray = false;
      if (locationSchemas[location]?.schemas) schemaArray = true;
      if (locationClusters[location]) clusterArray = true;

      let update = {};
      if (locationType === "snowflake") {
        const [schemasByDB, clusters] = await Promise.all([
          schemaArray ? locationSchemas[location]?.rawSchemas : getSchemaList(location),
          clusterArray ? locationClusters[location] : getExternalSnowflakeCluster(location),
        ]);

        const isSchemaExist = checkIsSchemaExist(schemasByDB, additionalState.database, additionalState.schema);
        if (!isSchemaExist) {
          update = {
            ...(location !== "SNOWFLAKE" ? { database: "", schema: "" } : { database: DBName, schema: defaultSchema }),
          };
        }

        const $defauleCluster = location === "SNOWFLAKE" ? defaultCluster : "";
        const selectedCuster = additionalState.Cluster || $defauleCluster;
        const isClusterExist = checkIsClusterExist(clusters, selectedCuster);

        setPrevState(setVariableError, {
          [selectedKey]: {
            ...variableError[selectedKey],
            schemaError: false,
            clusterError: !isClusterExist,
          },
        });

        update.Cluster = isClusterExist ? selectedCuster : "";
      }

      setAdditionalState((prev) =>
        prev.map((item) => (item.key === selectedKey ? { ...item, location, ...update } : item))
      );

      setPrevState(setJobState, { loading: false });
    },
    [
      setJobState,
      locations,
      locationSchemas,
      locationClusters,
      setAdditionalState,
      getSchemaList,
      getExternalSnowflakeCluster,
      additionalState.database,
      additionalState.schema,
      additionalState.Cluster,
      selectedKey,
      defaultCluster,
      setVariableError,
      variableError,
      DBName,
      defaultSchema,
    ]
  );

  const handleFileName = useCallback(
    (e) => {
      if (regexFileName(e.target.value)) {
        setAdditionalState((prev) =>
          prev.map((item) => (item.key === selectedKey ? { ...item, fileName: e.target.value } : item))
        );
      }
    },
    [selectedKey, setAdditionalState]
  );

  return (
    <>
      <Typography variant="body1" sx={{ minHeight: 40, color: "#ed6c02" }}>
        Select a section to edit its configuration
      </Typography>
      <RadioGroup row name="type" value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
        <Grid container direction="row" spacing={2}>
          {additionalDataSections.map(({ key, variable, ...other }, index) => {
            const { isMissingVariable, isDuplicate } = variableError[key] ?? {};

            let errorMessage = "";
            if (isMissingVariable) errorMessage = "Variable name is required";
            else if (isDuplicate) errorMessage = "Duplicate variable name found";

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel value={key} control={<Radio />} label="" sx={{ marginRight: 1 }} />
                  <TextField
                    required={other.includeResult && other.queryResultAs === 2}
                    variant="outlined"
                    inputProps={{ maxLength: 50 }}
                    fullWidth
                    label={`Variable Name for Section ${index + 1}`}
                    value={variable}
                    error={(isMissingVariable || isDuplicate) ?? false}
                    helperText={errorMessage}
                    onChange={(e) => {
                      const rawInput = e.target.value;

                      let value;

                      if (rawInput.length === 0) {
                        // If the user has deleted everything, reset to an empty state
                        value = "";
                      } else {
                        // Remove any existing $ signs first
                        const stripped = rawInput.replace(/\$/g, "");
                        // Wrap the stripped value with a $ at the start and end
                        value = `$${stripped}$`;
                      }

                      setAdditionalState((prev) =>
                        prev.map((item) => (item.key === key ? { ...item, variable: value } : item))
                      );
                    }}
                  />
                  <IconButton
                    className={classes.IconHolder}
                    onClick={() =>
                      setAdditionalState((prev) => {
                        const newSections = prev.filter((item) => item.key !== key);
                        setSelectedKey(newSections[0].key);
                        setVariableError((prev) => {
                          const { [key]: deleted, ...newObj } = prev;
                          return newObj;
                        });
                        return newSections;
                      })
                    }
                    style={{
                      marginLeft: "5px",
                      color: additionalDataSections.length === 1 ? undefined : "#C72C2C",
                    }}
                    disabled={additionalDataSections.length === 1}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </RadioGroup>

      <Typography variant="body1" className={classes.PanelTitle}>
        {`Configuration for Section ${section}`}
      </Typography>
      <Grid container direction={"row"} spacing={2} sx={{ mb: 2 }}>
        <Grid display="flex" size={12}>
          <div>
            <InfoOutlinedIcon style={{ marginRight: 5, verticalAlign: "bottom" }} />
            <span style={{ fontSize: 12 }}>
              Please ensure the result set is limited to a few thousand records to avoid any size-related issues.
              {additionalState.queryResultAs === 2 ? (
                <>
                  Also, make sure to include <b>{additionalState.variable || "$ADDITIONAL_DATA$"} </b> in message body
                  to specify the table location.
                </>
              ) : (
                ""
              )}
            </span>
          </div>
        </Grid>
        <Grid>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox />}
              value={additionalState.includeResult}
              checked={additionalState.includeResult}
              onChange={() =>
                setAdditionalState((prev) =>
                  prev.map((item) =>
                    item.key === selectedKey ? { ...item, includeResult: !item.includeResult } : item
                  )
                )
              }
              label="Include Query result as"
            />
          </FormGroup>
        </Grid>
        <Grid>
          <RadioGroup
            value={additionalState.queryResultAs}
            onChange={(e) =>
              setAdditionalState((prev) =>
                prev.map((item) =>
                  item.key === selectedKey ? { ...item, queryResultAs: Number(e.target.value) } : item
                )
              )
            }
            row
          >
            {Types.map((type) => (
              <FormControlLabel key={type.value} value={type.value} control={<Radio />} label={type.text} />
            ))}
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid container direction={"row"} spacing={2}>
        <Grid>
          <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
            <InputLabel required>Connection</InputLabel>
            <Select
              value={additionalState.location}
              label="Connection"
              required
              MenuProps={{ sx: { height: "220px" } }}
              onChange={(e) => handleConnectionChange(e.target.value)}
            >
              {locations.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid>
          <KMKGroupSelect
            options={locationSchemas[additionalState.location]?.schemas ?? []}
            label="Schema"
            value={{ title: additionalState.schema, groupBy: additionalState.database }}
            handleChange={handleSchemaChange}
            error={schemaError}
            errorText={"Schema does not exists or you do not have access to work on it."}
            noOptionsText={"No schema found"}
            className={classes.AutoComplete}
            PaperComponent={CustomPaper}
            required
            sx={{ width: { xs: "100%", sm: "300px" } }}
          />
        </Grid>

        <Grid>
          <FormControl required variant="outlined" sx={{ width: { xs: "100%", sm: "300px" } }}>
            <InputLabel>Compute Cluster</InputLabel>
            <Select
              value={additionalState.Cluster}
              onChange={handleClusterChange}
              label="Compute Cluster"
              MenuProps={{ sx: { height: "220px" } }}
              error={clusterError}
              errorText={"Cluster does not exists or you do not have access to work on it."}
            >
              {connectionClusters?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {additionalState.location === "SNOWFLAKE" && (
          <Grid>
            <RunJobAsComponent
              participants={participantsExcludeVisitor}
              selectedUser={additionalState.runJobAsUserId}
              handleChange={handleUserChange}
              handleRunUnderMyName={handleRunUnderMyName}
              error={(Job_id || Edit_Job_id) && userNotExist}
              editable={accessRole === 1 && participantsExcludeVisitor.length > 0}
              nonEditableValue={userNotExist ? "" : additionalState.runJobAsUserFullName}
              sx={{ width: { xs: "100%", sm: "300px" } }}
              fullWidth
            />
          </Grid>
        )}

        {additionalState.queryResultAs === 1 && (
          <Grid>
            <TextField
              value={additionalState.fileName || ""}
              sx={{ width: { xs: "100%", sm: "300px" } }}
              label="File Name"
              inputProps={{ maxLength: 50 }}
              onChange={handleFileName}
              error={fileNameError}
              helperText={fileNameError ? "Duplicate file name found" : "File name without extension"}
            />
          </Grid>
        )}
      </Grid>
      <Grid container direction={"row"} spacing={2}>
        <Grid
          sx={{ paddingTop: "10px !important", display: "flex", alignItems: "center", justifyContent: "space-between" }}
          size={12}
        >
          <Typography color={"#00000099"} padding={0} variant="subtitle2" component="span">
            {Job_id || Edit_Job_id
              ? `Query * (Last updated by: ${additionalState.queryLastUpdatedByName || "-"})`
              : `Query *`}
          </Typography>
        </Grid>
        <Grid sx={{ paddingTop: "2px !important" }} size={12}>
          <ComponentSuspense>
            <MyAceEditor
              fontSize={14}
              style={{ paddingTop: "10px" }}
              className={classes.queryArea}
              ref={queryRef}
              label="Query"
              value={additionalState.query}
              setQuery={(value) =>
                setAdditionalState((prev) =>
                  prev.map((item) => (item.key === selectedKey ? { ...item, query: value } : item))
                )
              }
              location={additionalState.location}
              type={locationType.toLowerCase() || ""}
              runParams={{
                runJobAsUserId:
                  (Job_id || Edit_Job_id) && additionalState.userNotExist ? null : additionalState.runJobAsUserId,
                database: additionalState.database,
                schema: additionalState.schema,
                cluster: additionalState.Cluster,
                refreshParam: selectedKey,
              }}
              additionalState={additionalState}
              onError={(error) =>
                setAdditionalState((prev) =>
                  prev.map((item) => (item.key === selectedKey ? { ...item, queryError: error } : item))
                )
              }
              queryLastUpdatedByName={additionalState.queryLastUpdatedByName}
            />
          </ComponentSuspense>
        </Grid>
      </Grid>
    </>
  );
};

AdditionalData.propTypes = {
  additionalDataSections: PropTypes.array.isRequired,
  additionalState: PropTypes.object,
  getSchemaList: PropTypes.func.isRequired,
  locationClusters: PropTypes.object,
  locationSchemas: PropTypes.object,
  locations: PropTypes.array.isRequired,
  participantsExcludeVisitor: PropTypes.array.isRequired,
  selectedKey: PropTypes.string.isRequired,
  setAdditionalState: PropTypes.func.isRequired,
  setJobState: PropTypes.func.isRequired,
  updateClusterList: PropTypes.func.isRequired,
  setSelectedKey: PropTypes.func.isRequired,
  setVariableError: PropTypes.func.isRequired,
  variableError: PropTypes.object,
  section: PropTypes.number,
};

export default React.memo(AdditionalData);
